import { type RefObject, useEffect } from "react";

/** The custom property the scale is kept in, for a stylesheet to draw the box at: `scale(var(--fit, 1))`. */
export const FIT_PROPERTY = "--fit";

/** The scale at which a box `natural` pixels tall fits a room `room` pixels tall: 1 when it already does. */
export function fitScale(room: number, natural: number): number {
  if (!(room > 0) || !(natural > 0)) return 1;
  // Rounded down to a thousandth, so the drawn box is never a hair taller than its room.
  return Math.min(1, Math.floor((room / natural) * 1000) / 1000);
}

/**
 * Keeps the fit property on `box` at the scale that fits its height in
 * `room`'s. A transform leaves layout alone, so the height read from the
 * box stays its natural one. Both elements are watched: the scale follows
 * the window, and anything that changes either height.
 */
export function useFitScale(room: RefObject<HTMLElement | null>, box: RefObject<HTMLElement | null>, on = true): void {
  useEffect(() => {
    const [outer, inner] = [room.current, box.current];
    if (!on || !outer || !inner) return;
    const fit = () => inner.style.setProperty(FIT_PROPERTY, String(fitScale(outer.clientHeight, inner.offsetHeight)));
    const observer = new ResizeObserver(fit);
    observer.observe(outer);
    observer.observe(inner);
    fit();
    return () => {
      observer.disconnect();
      inner.style.removeProperty(FIT_PROPERTY);
    };
  }, [room, box, on]);
}
